import { useCallback, useEffect, type ReactNode } from 'react'

import { useAuthContext } from '@space-shell/common/contexts/auth'
import useCustomEvent from '@space-shell/common/hooks/useCustomEvent'
import { useAuthStore, useUserInfoStore, useViewAsStore } from '@space-shell/core/store'
import { useLocation } from 'react-router-dom'

import { EventNames } from './types'

export type SessionEventsWrapperProps = {
  children: ReactNode
}

/**
 * WARNING: This wrapper should only be used to emit user session-related events.
 * This is a temporary solution to allow MFs to no longer directly rely on props
 * passed through providers via the host (`app-space`) and will eventually allow
 * apps to connect directly to `app-club`.
 */
const SessionEventsWrapper = ({ children }: SessionEventsWrapperProps) => {
  const { pathname } = useLocation()
  const { emitEvent } = useCustomEvent()
  const { id, token, authorities, userName, userSocialName, ucode } = useAuthStore()
  const { avatar, email } = useUserInfoStore()
  const { authStatus } = useAuthContext()
  const { from, isEnabled, role, classItem, isReadonly } = useViewAsStore()

  const emitUserDataEvent = useCallback(() => {
    emitEvent(EventNames.USER_SESSION_DATA, {
      id,
      token,
      authorities,
      name: userName,
      socialName: userSocialName,
      email: email ?? '',
      avatar,
      ucode,
      authStatus
    })
  }, [
    emitEvent,
    authorities,
    avatar,
    email,
    id,
    token,
    ucode,
    userName,
    userSocialName,
    authStatus
  ])

  useCustomEvent(EventNames.RELOAD_USER_SESSION_DATA, emitUserDataEvent)

  useEffect(() => {
    emitUserDataEvent()
  }, [pathname, emitUserDataEvent])

  useEffect(() => {
    emitEvent(EventNames.VIEW_AS_DATA, {
      from,
      isEnabled,
      role,
      classItem,
      isReadonly
    })
  }, [pathname, emitEvent, from, isEnabled, role, classItem, isReadonly])

  return <>{children}</>
}

export default SessionEventsWrapper
